// ============================================================================
// New Accounts — Needs Info (Cowork 02, 2026-06-28).
//
// A persistent, non-dismissable list of what's still missing on in-flight and
// just-started accounts. Two stacked sections:
//
//   1. Needs a customer — review-queue cards (materialize_status =
//      'needs_review') with a "Resolve (create / link)" action. Since
//      2026-09-10 the building already exists and is named on the row; what is
//      missing is whose building it is. They carry no requirement chips yet
//      because those hang off a settled account.
//
//   2. Open requirements — one card per materialized SOP/facility with open
//      requirements: KEY chips (red, non-dismissable until filled) + HELPFUL
//      chips (amber), the owner, days open, and a quick-fill deep link to the
//      facility detail.
//
// Visibility relies entirely on the underlying RLS (both views are
// security_invoker): leadership sees all, AMs see their own, finance/HR per
// their grants. No new RLS surface.
// ============================================================================

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button, EmptyState, ErrorState, Skeleton } from '../../shared/components';
import { daysUntilStart } from './lib/lifecycle';
import {
  useNeedsInfo,
  type NeedsInfoGroup,
  type OpenRequirement,
  type ReviewQueueRow,
} from './hooks/use-needs-info';
import { ReviewResolveDialog, type ReviewResolveTarget } from './review-resolve-dialog';

/** Days since a date string (created_date / start handling). null if no date. */
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const ms = Date.now() - t;
  return Math.max(0, Math.round(ms / 86_400_000));
}

function ownerLabel(req: OpenRequirement, names: Map<string, string>): string {
  if (req.owner_eid && names.has(req.owner_eid)) return names.get(req.owner_eid) as string;
  if (req.owner_role) {
    // Pretty-print the role token (leadership / finance / etc.).
    return req.owner_role.charAt(0).toUpperCase() + req.owner_role.slice(1);
  }
  return 'Unassigned';
}

function ReqChip({ req, tier }: { req: OpenRequirement; tier: 'key' | 'helpful' }) {
  const cls =
    tier === 'key'
      ? 'border-rag-red/30 bg-rag-red/5 text-rag-red'
      : 'border-st-warn-border-strong bg-st-warn-bg text-st-warn-fg-strong';
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${cls}`}
      title={req.label}
    >
      {req.label}
    </span>
  );
}

function GroupCard({ g }: { g: NeedsInfoGroup }) {
  const allOwners = useMemo(() => {
    const set = new Set<string>();
    for (const r of [...g.keyReqs, ...g.helpfulReqs]) set.add(ownerLabel(r, g.ownerNames));
    return Array.from(set);
  }, [g]);

  const to = g.facilityId
    ? `/account-management/facilities/${g.facilityId}`
    : '/new-accounts';

  return (
    <li className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-tcs-text">{g.displayName}</div>
          <div className="mt-0.5 text-[11px] text-tcs-text-secondary">
            {g.sopId}
            {g.facilityId ? ` · ${g.facilityId}` : ''} · owner: {allOwners.join(', ')}
          </div>
        </div>
        <Link
          to={to}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-tcs-border px-2 py-1 text-xs font-medium text-tcs-accent-ink hover:bg-tcs-bg-muted"
        >
          Quick-fill <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {g.keyReqs.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-rag-red">
            <AlertTriangle className="h-3 w-3" /> Required ({g.keyReqs.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {g.keyReqs.map((r) => (
              <ReqChip key={r.req} req={r} tier="key" />
            ))}
          </div>
        </div>
      )}

      {g.helpfulReqs.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-st-warn-fg">
            Helpful ({g.helpfulReqs.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {g.helpfulReqs.map((r) => (
              <ReqChip key={r.req} req={r} tier="helpful" />
            ))}
          </div>
        </div>
      )}
    </li>
  );
}

function ReviewRow({
  row,
  onResolve,
}: {
  row: ReviewQueueRow;
  onResolve: (t: ReviewResolveTarget) => void;
}) {
  const dStart = daysUntilStart({ start_date: row.start_date });
  const startChip =
    dStart === null
      ? null
      : dStart < 0
        ? `${Math.abs(dStart)}d overdue`
        : dStart === 0
          ? 'Starts today'
          : `Starts in ${dStart}d`;
  const age = daysSince(row.created_date);

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-st-warn-border-strong bg-st-warn-bg/50 p-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-tcs-text">{row.display_name}</div>
        <div className="mt-0.5 text-[11px] text-tcs-text-secondary">
          {row.sop_id}
          {row.facility_id ? ` · ${row.facility_id}` : ''}
          {row.market_region ? ` · ${row.market_region}` : ''}
          {startChip ? ` · ${startChip}` : ''}
          {age !== null ? ` · open ${age}d` : ''}
        </div>
      </div>
      <Button
        size="sm"
        variant="secondary"
        onClick={() =>
          onResolve({
            sopId: row.sop_id,
            displayName: row.display_name,
            facilityId: row.facility_id,
          })
        }
      >
        Resolve
      </Button>
    </li>
  );
}

export default function NeedsInfoBoard() {
  const { groups, reviewQueue, isLoading, error, refetch } = useNeedsInfo();
  const [resolveTarget, setResolveTarget] = useState<ReviewResolveTarget | null>(null);

  const keyCount = useMemo(
    () => groups.reduce((sum, g) => sum + g.keyReqs.length, 0),
    [groups],
  );

  if (isLoading && groups.length === 0 && reviewQueue.length === 0) {
    return (
      <div className="space-y-2">
        <Skeleton block height={64} />
        <Skeleton block height={64} />
        <Skeleton block height={64} />
      </div>
    );
  }

  if (error) {
    return <ErrorState body={error} onRetry={refetch} />;
  }

  // ⛔ Every source this board renders counts toward "is there anything here" —
  // a confident empty state over the top of real work is worse than no empty
  // state. (The card-versus-building section that used to be counted here
  // retired on 2026-09-11: there is one record now, so a card and its building
  // have nothing left to disagree about.)
  const nothing = groups.length === 0 && reviewQueue.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-tcs-text-secondary">
          {groups.length} account{groups.length === 1 ? '' : 's'} with open info
          {keyCount > 0 ? ` · ${keyCount} required item${keyCount === 1 ? '' : 's'}` : ''}
          {reviewQueue.length > 0 ? ` · ${reviewQueue.length} needs a customer` : ''}
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={refetch}
          disabled={isLoading}
          icon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Refresh
        </Button>
      </div>

      {nothing && (
        <EmptyState
          icon={<ShieldCheck className="h-6 w-6" />}
          title="Nothing missing"
          body="Every visible new account has its required and helpful info recorded, and every card agrees with the building it created."
        />
      )}

      {/* The card no longer stops being the truth the instant it is submitted.
          Where it and its building still disagree, that shows here instead of
          waiting for somebody to notice by eye. */}
      {reviewQueue.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-st-warn-fg">
            Needs a customer ({reviewQueue.length})
          </h2>
          <ul className="space-y-2">
            {reviewQueue.map((r) => (
              <ReviewRow key={r.sop_id} row={r} onResolve={setResolveTarget} />
            ))}
          </ul>
        </section>
      )}

      {groups.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-secondary">
            Open requirements ({groups.length})
          </h2>
          <ul className="space-y-2">
            {groups.map((g) => (
              <GroupCard key={g.sopId} g={g} />
            ))}
          </ul>
        </section>
      )}

      {resolveTarget && (
        <ReviewResolveDialog
          target={resolveTarget}
          onClose={() => setResolveTarget(null)}
          onResolved={refetch}
        />
      )}
    </div>
  );
}
